// Example:
//
// const pack = await AudioManager.instance.load({
//   moveStart: '/sounds/move_start.mp3',
//   moveEnd: '/sounds/move_end.mp3',
// });
//
// await pack.moveStart.play();
// pack.moveStart.stop();
//
// AudioManager.instance.stopAll();

/*
    ____,-------------------------------,____
    \   |          Interfaces           |   /
    /___|-------------------------------|___\
*/

export type IAudioPack<T extends string> = {
  [key in T]: ISound;
};

export interface ISound {
  isPlaying: boolean;
  play(volume?: number): Promise<void>;
  stop(): void;
}

/*
    ____,-------------------------------,____
    \   |            Manager            |   /
    /___|-------------------------------|___\
*/

export class AudioManager {
  private constructor() {
    this.ctx = this.createCtx();
  }

  static #instance?: AudioManager;

  static get instance(): AudioManager {
    if (!this.#instance) {
      this.#instance = new AudioManager();
    }

    return this.#instance;
  }

  private ctx: AudioContext;

  sounds = new Map<string, Sound>();

  get state() {
    return this.ctx.state;
  }

  async load<T extends string>(resources: Record<T, string>): Promise<IAudioPack<T>> {
    const pack = {} as IAudioPack<T>;
    const names = Object.keys(resources) as T[];

    const tasks: Promise<unknown>[] = [];

    for (const name of names) {
      const url = resources[name];

      if (!this.sounds.has(url)) {
        const task = fetch(url).then(
          async (res) => {
            if (!res.ok) {
              const err = new Error(`Could not load audio at url ${url}. Status: ${res.status}`);
              err.name = 'AudioManagerError';

              throw err;
            }

            const buffer = await this.ctx.decodeAudioData(await res.arrayBuffer());

            this.sounds.set(url, new Sound(this.ctx, buffer));

            pack[name] = this.sounds.get(url)!;
          },
          (e) => {
            const err = new Error(`Could not load audio at url ${url}`, {
              cause: e,
            });
            err.name = 'AudioManagerError';

            throw err;
          },
        );

        tasks.push(task);
      } else {
        pack[name] = this.sounds.get(url)!;
      }
    }

    await Promise.all(tasks);

    return pack;
  }

  stopAll() {
    this.sounds.forEach((s) => s.stop());
  }

  private createCtx() {
    const ctx = new AudioContext({ latencyHint: 'interactive' });
    this.ctx = ctx;

    // ручной запуск контекста
    if (ctx.state === 'suspended') {
      const activityAC = new AbortController();
      const events = [
        'pointerdown',
        /* TODO: скорее всего не работает в большинстве браузеров */ 'touchend',
        'click',
        'keydown',
      ] as const;

      events.forEach((evt) => {
        window.addEventListener(
          evt,
          () => {
            if (ctx.state !== 'suspended') {
              activityAC.abort();
              return;
            }

            ctx.resume().then(() => {
              if (ctx.state !== 'suspended') {
                activityAC.abort();
              }
            });
          },
          {
            capture: true,
            signal: activityAC.signal,
          },
        );
      });
    }

    // пересоздание контекста
    const stateAC = new AbortController();
    ctx.addEventListener(
      'statechange',
      () => {
        if (ctx.state !== 'suspended' && ctx.state !== 'running') {
          stateAC.abort();
          ctx.close();

          this.ctx = this.createCtx();

          this.sounds.forEach((s) => {
            s.updateContext(this.ctx);
          });
        }
      },
      { signal: stateAC.signal },
    );

    return ctx;
  }
}

/*
    ____,-------------------------------,____
    \   |        Sound (internal)       |   /
    /___|-------------------------------|___\
*/

class Sound implements ISound {
  constructor(
    private ctx: AudioContext,
    private buffer: AudioBuffer,
  ) {
    this.node = ctx.createGain();
    this.connected = false;
  }

  private node: GainNode;
  private source?: AudioBufferSourceNode;
  private connected: boolean;

  get isPlaying() {
    return Boolean(this.source);
  }

  async play(volume = 1) {
    if (this.ctx.state !== 'running') {
      return;
    }

    if (!this.connected) {
      this.node.connect(this.ctx.destination);
      this.connected = true;
    }

    this.node.gain.setValueAtTime(volume, this.ctx.currentTime);

    const source = this.ctx.createBufferSource();
    source.buffer = this.buffer;
    source.connect(this.node);

    this.source = source;

    const dispose = () => {
      source.disconnect();
      if (source === this.source) this.source = undefined;
    };

    return new Promise<void>((r) => {
      source.onended = () => {
        dispose();
        r();
      };

      try {
        source.start(0);
      } catch (e) {
        dispose();

        throw e;
      }
    });
  }

  stop() {
    if (!this.source) {
      return;
    }

    this.source.stop();
    this.source.disconnect();
    this.source = undefined;
  }

  updateContext(nextContext: AudioContext) {
    this.stop();

    if (this.connected) {
      this.node.disconnect();
      this.connected = false;
    }

    const nextNode = nextContext.createGain();

    this.node = nextNode;
    this.ctx = nextContext;
  }
}
